import { computed, readonly, ref, shallowRef } from "vue";
import { getOrders } from "@/core/api/graphql/orders";
import { DEFAULT_ORDERS_PER_PAGE, TAB_BUCKET_ORDERS_FACET_NAME } from "@/core/constants";
import { SortDirection } from "@/core/enums";
import { Sort } from "@/core/types";
import { Logger, toEndDateFilterValue, toStartDateFilterValue } from "@/core/utilities";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { useUserOrdersFilter } from "./useUserOrdersFilter";
import type { OrdersFilterData } from "../types";
import type { CustomerOrderTypeExtension, FacetTermType } from "@/core/api/graphql/types";
import type { MaybeRef, Ref } from "vue";

export interface IUseUserOrdersOptions {
  itemsPerPage?: MaybeRef<number>;
  isQuotesPage?: boolean;
}

const facets = shallowRef<FacetTermType[] | undefined>();

export function useUserOrders(options: IUseUserOrdersOptions) {
  const itemsPerPage = ref(options.itemsPerPage ?? DEFAULT_ORDERS_PER_PAGE);
  const { defaultTabBucketTLFilter } = useUserOrdersFilter(options.isQuotesPage);

  const orders: Ref<CustomerOrderTypeExtension[]> = shallowRef<CustomerOrderTypeExtension[]>([]);
  const totalCount: Ref<number> = ref(0);
  const loading: Ref<boolean> = ref(false);
  const pages: Ref<number> = ref(0);
  const page: Ref<number> = ref(1);
  const keyword: Ref<string> = ref("");

  function getFilterExpression(filterData: OrdersFilterData, isTabbedOrdersRequest: boolean): string {
    let filterExpression = "";
    if (keyword.value) {
      filterExpression += `${keyword.value} `;
    }

    if (isTabbedOrdersRequest) {
      const tabBucketTLFilter = filterData.tabBucketTL.length ? filterData.tabBucketTL : defaultTabBucketTLFilter();
      const tabBuckets = tabBucketTLFilter.map((status) => `"${status}"`);
      filterExpression += `tabbuckettl:${tabBuckets.join(",")} `;
    }

    const startDateFilterValue = filterData.startDate ? toStartDateFilterValue(filterData.startDate) : "";
    const endDateFilterValue = filterData.endDate ? toEndDateFilterValue(filterData.endDate) : "";
    if (startDateFilterValue || endDateFilterValue) {
      let createdDateFilterValue = "";
      if (startDateFilterValue) {
        createdDateFilterValue += `"${startDateFilterValue}" `;
      }
      if (endDateFilterValue) {
        createdDateFilterValue += "TO";
        createdDateFilterValue += ` "${endDateFilterValue}"`;
      }
      filterExpression += `createddate:[${createdDateFilterValue}]`;
    }

    filterExpression = filterExpression.trim();

    return filterExpression;
  }

  const sort: Ref<Sort> = ref(new Sort("createdDate", SortDirection.Descending));

  async function fetchOrders(currentFilterData: OrdersFilterData | undefined, isTabbedOrdersRequest: boolean = false) {
    loading.value = true;

    const filterExpression = currentFilterData ? getFilterExpression(currentFilterData, isTabbedOrdersRequest) : "";

    try {
      if (punchoutCookies && punchoutCookies.value && punchoutCookies.value.signedInWithFallbackCredentials) {
        return;
      }
      const response = await getOrders({
        sort: sort.value.toString(),
        first: itemsPerPage.value,
        after: String((page.value - 1) * itemsPerPage.value),
        filter: filterExpression,
        facet: TAB_BUCKET_ORDERS_FACET_NAME,
      });
      orders.value = response.items ?? [];
      totalCount.value = response.totalCount ?? 0;
      pages.value = Math.ceil((response.totalCount ?? 0) / itemsPerPage.value);
      facets.value = response.term_facets?.find((item) => item.name === TAB_BUCKET_ORDERS_FACET_NAME)?.terms;
    } catch (e) {
      Logger.error(`${useUserOrders.name}.${fetchOrders.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchTabbedOrders(currentFilterData: OrdersFilterData | undefined) {
    await fetchOrders(currentFilterData, true);
  }

  return {
    sort,
    fetchOrders,
    fetchTabbedOrders,
    totalCount,
    loading: readonly(loading),
    orders: computed(() => orders.value),
    facets: computed(() => facets.value),
    itemsPerPage,
    pages,
    page,
    keyword,
  };
}
